import React from 'react';
import { Button } from '@americanexpress/dls-react';
import {
  cn,
  getStickyGlobalHeaderOffset,
  smoothScrollToElement,
} from '@/helpers/utils';
import { useAnalyticsContext } from '@/context/analyticsContext';

interface PrimaryButton {
  href?: string;
  text?: string;
  clickEvent?: {
    detail?: string;
    element?: string;
  };
}

interface HeroContent {
  backgroundImageUrl?: string;
  // Resolved video URL (string) passed from parent
  backgroundVideoUrl?: string;
  bannerImageUrl?: string;
  bannerImageAlt?: string;
  bannerText?: string;
  bannerSubText?: string;
  primaryButton?: PrimaryButton;
}

export const CenturionNetworkHero: React.FC<{ content?: HeroContent }> = ({
  content = {},
}) => {
  const { customTrack } = useAnalyticsContext();
  const {
    backgroundImageUrl = '',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- added for use in an upcoming enhancement
    backgroundVideoUrl = '',
    bannerImageUrl = '',
    bannerImageAlt = '',
    bannerText = '',
    bannerSubText = '',
    primaryButton = {
      href: '',
      text: '',
      clickEvent: {
        detail: '',
        element: '',
      },
    },
  } = content;
  const detail = primaryButton?.clickEvent?.detail;
  const element = primaryButton?.clickEvent?.element;

  return (
    <div
      id="centurion-network-hero"
      className={cn(
        'tw-bg-center tw-relative tw-h-135 tw-max-h-135 tw-w-full tw-overflow-hidden tw-bg-cover tw-bg-no-repeat md_tw-h-145 md_tw-max-h-145 lg_tw-h-180 lg_tw-max-h-180 xl_tw-h-202dot5 xl_tw-max-h-202dot5'
      )}
      role="img"
      aria-label="Centurion Lounge ATL Lifestyle Reception"
      style={{
        backgroundImage: `url('${backgroundImageUrl}')`,
        backgroundPosition: 'center center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-z-10">
        <div
          aria-hidden="true"
          className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0"
          style={{
            top: '-80px',
            background:
              'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.50) 100%)',
            backdropFilter: 'blur(4px)',
            WebkitMaskImage:
              'linear-gradient(to bottom, transparent 0%, black 40%)',
            maskImage: 'linear-gradient(to bottom, transparent 0%, black 40%)',
          }}
        />
        <div className="tw-relative tw-w-full tw-px-4 tw-pb-10 tw-pt-6 md_tw-px-6 lg_tw-px-8 xl_tw-px-10 x2l_tw-px-0">
          <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-screen-xl tw-flex-col tw-items-center tw-gap-4 md_tw-flex-row md_tw-items-center md_tw-justify-between">
            <div className="tw-flex tw-w-full tw-items-center tw-justify-start tw-gap-4 tw-rounded-2xl tw-bg-gray-200 tw-px-6 tw-py-4 md_tw-w-auto md_tw-flex-none">
              <div className="tw-flex-shrink-0">
                <img
                  src={bannerImageUrl}
                  alt={bannerImageAlt}
                  className="tw-h-10 tw-w-auto"
                />
              </div>
              <div className="tw-flex tw-flex-col">
                <span className="tw-text-base tw-text-gray-800">
                  {bannerText}
                </span>
                <span className="tw-text-xs tw-text-gray-700">
                  {bannerSubText}
                </span>
              </div>
            </div>

            {/* Learn More button, centered below banner */}
            <div className="tw-flex tw-w-full tw-justify-center md_tw-w-auto md_tw-flex-shrink-0 md_tw-items-center md_tw-justify-end">
              {primaryButton?.href && primaryButton?.text && (
                <Button
                  className={cn(
                    'btn primary btn-primary flex-justify-center dls-white tw-my-2dot5 tw-inline-flex tw-w-full tw-max-w-300 tw-items-center tw-font-medium tw-text-link md_tw-w-auto'
                  )}
                  onClick={(event: React.MouseEvent<HTMLElement>) => {
                    const scrollTargetElement = primaryButton?.href
                      ? document.querySelector(primaryButton.href)
                      : null;
                    if (scrollTargetElement) {
                      smoothScrollToElement({
                        element: scrollTargetElement,
                        offset: getStickyGlobalHeaderOffset(),
                      });
                    }

                    if (detail && element) {
                      customTrack({
                        event,
                        detail,
                        element,
                      });
                    }
                  }}
                >
                  {primaryButton.text}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
